import { upperFirst, isBoolean, isString } from 'lodash';
import {
  IUseTablePreserverDecoderConfigItem,
  TUseTablePreserverParser,
  TUseTablePreserverIsValid,
} from './interfaces';
import {
  SecurityScoreNameEnum,
  PageSizeOptionEnum,
  TablePreserverDecoderConfigItemTypeEnum as ConfigItemTypeEnum,
  PasswordStrengthNameEnum,
} from '../../enums';
import { IISODateRange, isISODateValid } from '../../services';
import TablePreserverCommonInitialTableValues from './TablePreserverCommonInitialTableValues';

const isEnumValueValid = (enumToCheck: any) => (value: any) => !!enumToCheck[upperFirst(value)];
const isColumnOrderDirectionValid = (value: string) => (
  value === '' || value === 'asc' || value === 'desc'
);
const isDateRangeValid = (dateRange: IISODateRange | undefined) => {
  if (!dateRange) {
    return false;
  }
  if (!dateRange.from || !dateRange.to) {
    return false;
  }
  return isISODateValid(dateRange.from) && isISODateValid(dateRange.to);
};

export const tablePreserverDefaultParser: TUseTablePreserverParser = (value) => value;
export const tablePreserverDefaultIsValid: TUseTablePreserverIsValid = () => true;

interface ICommonBuilderConfigOption {
  fallbackValue?: any;
}
interface IBooleanConfigOption extends ICommonBuilderConfigOption {}
interface IEnumConfigOption extends ICommonBuilderConfigOption {
  enumToCheck: any;
  parser?: TUseTablePreserverParser;
}
interface IObjectConfigOption extends ICommonBuilderConfigOption {
  isValid?: TUseTablePreserverIsValid;
}
interface IStringConfigOption extends ICommonBuilderConfigOption {
  parser?: TUseTablePreserverParser;
  isValid?: TUseTablePreserverIsValid;
}

type TOption = IBooleanConfigOption | IEnumConfigOption | IObjectConfigOption;

export function buildTablePreserverDecoderConfigItem(
  type: ConfigItemTypeEnum.BOOLEAN,
  option?: IBooleanConfigOption,
): IUseTablePreserverDecoderConfigItem;
export function buildTablePreserverDecoderConfigItem(
  type: ConfigItemTypeEnum.ENUM,
  option: IEnumConfigOption
): IUseTablePreserverDecoderConfigItem;
export function buildTablePreserverDecoderConfigItem(
  type: ConfigItemTypeEnum.OBJECT,
  option?: IObjectConfigOption,
): IUseTablePreserverDecoderConfigItem;
export function buildTablePreserverDecoderConfigItem(
  type: ConfigItemTypeEnum.STRING,
  option?: IStringConfigOption,
): IUseTablePreserverDecoderConfigItem;

export function buildTablePreserverDecoderConfigItem(
  type: ConfigItemTypeEnum,
  option: TOption = {},
) {
  if (type === ConfigItemTypeEnum.BOOLEAN) {
    const {
      fallbackValue,
    } = option as IBooleanConfigOption;
    return {
      parser: JSON.parse,
      fallbackValue,
      isValid: isBoolean,
    };
  }
  if (type === ConfigItemTypeEnum.ENUM) {
    const {
      enumToCheck,
      parser,
      fallbackValue,
    } = option as IEnumConfigOption;
    return {
      parser,
      fallbackValue,
      isValid: isEnumValueValid(enumToCheck),
    };
  }
  if (type === ConfigItemTypeEnum.OBJECT) {
    const {
      fallbackValue,
      isValid,
    } = option as IObjectConfigOption;
    return {
      parser: JSON.parse,
      fallbackValue,
      isValid,
    };
  }
  if (type === ConfigItemTypeEnum.STRING) {
    const {
      parser,
      fallbackValue,
      isValid = tablePreserverDefaultIsValid,
    } = option as IStringConfigOption;
    return {
      parser,
      fallbackValue,
      isValid: (value: unknown) => isString(value) && isValid(value),
    };
  }

  const supportedTypes = Object.values(ConfigItemTypeEnum).join(', ');
  throw new Error(`Unsupported type '${type}' provided. Supported types: ${supportedTypes}`);
}

const activePageDecoderConfig: IUseTablePreserverDecoderConfigItem = {
  fallbackValue: TablePreserverCommonInitialTableValues.activePage,
  parser: (value) => parseInt(value, 10),
  isValid: (value) => !Number.isNaN(value) && value > 0,
};

const perPageDecoderConfig = buildTablePreserverDecoderConfigItem(ConfigItemTypeEnum.ENUM, {
  enumToCheck: PageSizeOptionEnum,
  fallbackValue: TablePreserverCommonInitialTableValues.perPage,
  parser: (value) => parseInt(value, 10),
});

const searchQueryDecoderConfig = buildTablePreserverDecoderConfigItem(ConfigItemTypeEnum.STRING, {
  fallbackValue: TablePreserverCommonInitialTableValues.searchQuery,
});

const columnOrderDirectionDecoderConfig: IUseTablePreserverDecoderConfigItem = {
  fallbackValue: TablePreserverCommonInitialTableValues.columnOrderDirection,
  isValid: isColumnOrderDirectionValid,
};

const securityScoreNameDecoderConfig = buildTablePreserverDecoderConfigItem(
  ConfigItemTypeEnum.ENUM,
  {
    enumToCheck: SecurityScoreNameEnum,
    fallbackValue: TablePreserverCommonInitialTableValues.securityScoreName,
  },
);

const dateRangeDecoderConfig = buildTablePreserverDecoderConfigItem(ConfigItemTypeEnum.OBJECT, {
  isValid: isDateRangeValid,
  fallbackValue: TablePreserverCommonInitialTableValues.dateRange,
});

const employeeAccountDecoderConfig = buildTablePreserverDecoderConfigItem(
  ConfigItemTypeEnum.OBJECT,
  {
    fallbackValue: TablePreserverCommonInitialTableValues.employeeAccount,
  },
);

const applicationNameDecoderConfig = buildTablePreserverDecoderConfigItem(
  ConfigItemTypeEnum.STRING,
  {
    fallbackValue: TablePreserverCommonInitialTableValues.applicationName,
  },
);

const passwordStrengthNameDecoderConfig = buildTablePreserverDecoderConfigItem(
  ConfigItemTypeEnum.ENUM,
  {
    enumToCheck: PasswordStrengthNameEnum,
    fallbackValue: TablePreserverCommonInitialTableValues.passwordStrengthName,
  },
);

const COMMON_CONFIGS = {
  activePage: activePageDecoderConfig,
  perPage: perPageDecoderConfig,
  searchQuery: searchQueryDecoderConfig,
  columnOrderDirection: columnOrderDirectionDecoderConfig,
  securityScoreName: securityScoreNameDecoderConfig,
  dateRange: dateRangeDecoderConfig,
  employeeAccount: employeeAccountDecoderConfig,
  applicationName: applicationNameDecoderConfig,
  passwordStrengthName: passwordStrengthNameDecoderConfig,
};

export const getTablePreserverDecoderCommonConfig = (
  name: keyof typeof COMMON_CONFIGS | string,
): IUseTablePreserverDecoderConfigItem => {
  const config = COMMON_CONFIGS[name as keyof typeof COMMON_CONFIGS];

  if (!config) {
    throw new Error(`Config for '${name}' is missing in common configs.`);
  }

  return config;
};
